'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import { getActivity, getServerActivity, subscribeActivity } from '@/lib/activity';

const SHOW_DELAY = 120;       // ms - skip the bar entirely for near-instant requests
const PILL_DELAY = 450;       // ms - only call it "saving" when it is actually slow
const ROUTE_TIMEOUT = 8000;   // ms - never leave the bar stuck if navigation is cancelled

/**
 * Global activity feedback, mounted once in the root layout:
 *   - a slim progress bar while any request is in flight or a page is navigating
 *   - a "Saving changes..." pill when a write (POST/PUT/PATCH/DELETE) is slow
 *
 * Because it listens to the axios layer and to link clicks, every screen gets
 * feedback without wiring up its own indicator.
 */
export default function TopProgressBar() {
  const { requests, mutations } = useSyncExternalStore(subscribeActivity, getActivity, getServerActivity);
  const pathname = usePathname();

  const [navigating, setNavigating] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | running | finishing
  const [progress, setProgress] = useState(0);
  const [pill, setPill] = useState(false);

  const busy = requests > 0 || navigating;
  const showTimer = useRef(null);
  const trickle = useRef(null);
  const finishTimer = useRef(null);
  const routeTimer = useRef(null);

  // Start navigating as soon as an internal link is clicked (Next gives no router events).
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      let url;
      try { url = new URL(a.href, window.location.href); } catch { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      setNavigating(true);
      clearTimeout(routeTimer.current);
      routeTimer.current = setTimeout(() => setNavigating(false), ROUTE_TIMEOUT);
    };
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
      clearTimeout(routeTimer.current);
    };
  }, []);

  // The new route has rendered.
  useEffect(() => {
    setNavigating(false);
    clearTimeout(routeTimer.current);
  }, [pathname]);

  // Drive the bar.
  useEffect(() => {
    if (busy) {
      clearTimeout(finishTimer.current);
      if (phase === 'idle' || phase === 'finishing') {
        clearTimeout(showTimer.current);
        showTimer.current = setTimeout(() => {
          setProgress(12);
          setPhase('running');
        }, phase === 'finishing' ? 0 : SHOW_DELAY);
      }
    } else {
      clearTimeout(showTimer.current);
      if (phase === 'running') {
        setProgress(100);
        setPhase('finishing');
        finishTimer.current = setTimeout(() => {
          setPhase('idle');
          setProgress(0);
        }, 380);
      }
    }
    return () => clearTimeout(showTimer.current);
  }, [busy, phase]);

  // Trickle toward 90% so it always looks alive but never "completes" early.
  useEffect(() => {
    if (phase !== 'running') return undefined;
    trickle.current = setInterval(() => {
      setProgress((p) => (p >= 90 ? p : p + (90 - p) * 0.12 + 0.4));
    }, 250);
    return () => clearInterval(trickle.current);
  }, [phase]);

  // "Saving..." pill for slow writes.
  useEffect(() => {
    if (mutations > 0) {
      const t = setTimeout(() => setPill(true), PILL_DELAY);
      return () => clearTimeout(t);
    }
    setPill(false);
    return undefined;
  }, [mutations]);

  // Don't let a half-finished write get closed by accident.
  useEffect(() => {
    if (mutations === 0) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [mutations]);

  useEffect(() => () => {
    clearTimeout(showTimer.current);
    clearTimeout(finishTimer.current);
    clearInterval(trickle.current);
  }, []);

  return (
    <>
      <div
        className={`ui-topbar ${phase !== 'idle' ? 'is-active' : ''} ${phase === 'finishing' ? 'is-finishing' : ''}`}
        role="progressbar"
        aria-hidden={phase === 'idle'}
        aria-label="Loading"
      >
        <div className="ui-topbar-fill" style={{ transform: `scaleX(${progress / 100})` }} />
      </div>
      {pill && (
        <div className="ui-activity-pill" role="status" aria-live="polite">
          <span className="ui-spinner" aria-hidden="true" />
          Saving changes...
        </div>
      )}
    </>
  );
}
